import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import { sendPrintJob } from './services/printService';

// Update the separator label generation function to accept a separator number
const generateSeparatorLabel = (doc, separatorNumber) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerY = pageHeight / 2;
  
  // Add text above line with separator number
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`SEP #${separatorNumber}`, pageWidth / 2, centerY - 0.15, { align: 'center' });
  
  // Draw thick line
  doc.setLineWidth(0.05);
  doc.setDrawColor(0, 0, 0);
  doc.line(0.125, centerY, pageWidth - 0.125, centerY);
  
  // Add text below line with separator number
  doc.text(`DO NOT USE`, pageWidth / 2, centerY + 0.25, { align: 'center' });
};

// Add this function to generate a filler label
const generateFillerLabel = (doc, fillerType) => {
  if (fillerType === 'ready') {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Fill entire label with black background
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Create random noise pattern - EXACTLY matching PDF Design 5
    // PDF uses 2 point pixels, which is approximately 0.0278 inches (2/72)
    const pixelSize = 2 / 72; // 2 points converted to inches (same as PDF)
    doc.setFillColor(255, 255, 255);

    for (let x = 0; x < pageWidth; x += pixelSize) {
      for (let y = 0; y < pageHeight; y += pixelSize) {
        if (Math.random() > 0.5) {
          doc.rect(x, y, pixelSize, pixelSize, 'F');
        }
      }
    }

    // Draw white rectangle in center with padding for text
    // PDF uses: width=96, height=20, x=labelWidth/2-48, y=labelHeight/2-9 (in 144x72 points = 2x1 inches)
    // For our 2.25x1.25 label, scale proportionally
    const pdfLabelWidth = 2.0; // 2 inch width in PDF
    const pdfLabelHeight = 1.0; // 1 inch height in PDF
    const textBoxWidthPDF = 96 / 72; // Convert 96 points to inches
    const textBoxHeightPDF = 20 / 72; // Convert 20 points to inches

    const textBoxX = (pageWidth - textBoxWidthPDF) / 2;
    const textBoxY = (pageHeight - textBoxHeightPDF) / 2;

    doc.setFillColor(255, 255, 255);
    doc.rect(textBoxX, textBoxY, textBoxWidthPDF, textBoxHeightPDF, 'F');

    // Add text centered on the white box - matching PDF font size (10pt in PDF)
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('READY TO SHIP', pageWidth / 2, pageHeight / 2 + 0.015, { align: 'center' });
  } else if (fillerType === 'set') {
    doc.setFontSize(16);
    doc.text('SOLD AS A SET', 1.125, 0.625, { align: 'center' });
    doc.setFontSize(12);
    doc.text('DO NOT SEPARATE', 1.125, 0.875, { align: 'center' });
  } else if (fillerType === 'warning') {
    doc.setFontSize(9);
    const warningText = `
      WARNING: To avoid danger of suffocation, keep plastic bags away from babies and children.  Do not use this bag in cribs, beds, carriages, or play pens. This bag is not a toy.
    `;
    doc.text(warningText, 0.125, 0.2, { maxWidth: 2 });
  } else if (fillerType === 'suffocation') {
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('WARNING SUFFOCATION', 1.125, 0.3, { align: 'center' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    // English text
    doc.text('Plastic bags and film can be dangerous. To avoid danger of', 1.125, 0.45, { align: 'center' });
    doc.text('suffocation, keep this bag or film away from babies and children.', 1.125, 0.6, { align: 'center' });
    
    // French text
    doc.text('Les sacs et les pellicules de plastique peuvent être dangereux.', 1.125, 0.8, { align: 'center' });
    doc.text('Pour éviter tout risque d\'étouffement, tenir ce sac ou cette', 1.125, 0.95, { align: 'center' });
    doc.text('pellicule hors de la portée des bébés et des enfants.', 1.125, 1.1, { align: 'center' });
  }
}; 


export const generatePDF = (item, quantity, expiryDate, option, separatorInterval = 0, sequenceSize = 0, printer = null, onPrintCallback = null, clientInfo = null, countryOfOrigin = '') => {
  // Get FNSKU first, before doing anything else
  let fnsku;
  try {
    // First try to get from cache
    const cacheKey = 'fnsku_cache';
    const cache = JSON.parse(localStorage.getItem(cacheKey) || '{}');
    const sku = item?.item?.merchant_sku;
    
    if (cache[sku]?.fnsku) {
      fnsku = cache[sku].fnsku;
    } else {
      // Try all possible locations for FNSKU
      fnsku = item?.item?.fnsku || // Direct FNSKU property
              item?.fnsku || // Direct on item
              item?.item?.identifiers?.find?.(id => id.identifier_type === 'FNSKU')?.identifier || // In identifiers
              item?.item?.identifiers?.find?.(id => id.type === 'FNSKU')?.identifier; // Alternative identifier format
    }

    if (!fnsku || fnsku === 'UNKNOWN') {
      throw new Error('No valid FNSKU found for this item');
    }
  } catch (error) {
    console.error('Error retrieving FNSKU:', error);
    if (onPrintCallback) {
      onPrintCallback({
        success: false,
        message: 'No valid FNSKU found for this item. Please ensure FNSKU is synced before printing.'
      });
    }
    return;
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'in',
    format: [2.25, 1.25],
  });

  const canvas = document.createElement('canvas');
  canvas.width = 360;
  canvas.height = 72;

  let labelCount = 0;
  let separatorCount = 0;
  const fillerTypes = ['ready', 'set', 'warning'];
  
  for (let i = 0; i < quantity; i++) {
    // Add separator page if needed
    if (separatorInterval > 0 && labelCount > 0 && labelCount % separatorInterval === 0) {
      separatorCount++;
      doc.addPage();
      generateSeparatorLabel(doc, separatorCount);
    }

    // Add new page for the next label (except for the first label)
    if (i > 0) {
      doc.addPage();
    }

    const condition = "New";

    // Generate the barcode using our retrieved FNSKU
    JsBarcode(canvas, fnsku, {
      format: "CODE128",
      displayValue: false,
      margin: 0,
    });

    const barcodeDataUrl = canvas.toDataURL('image/png');

    // Calculate layout - same for all shipments
    const expiryDateText = expiryDate ? `, Exp: ${expiryDate.toISOString().split('T')[0]}` : '';
    const conditionText = `${condition}${expiryDateText}`;

    // Standard layout: barcode at top, more space for product name
    const barcodeYPosition = 0.15;
    const fnskuYPosition = 0.6;
    const productNameStartY = 0.69;

    const pageWidth = doc.internal.pageSize.getWidth();
    const leftMargin = 0.125;
    const rightMargin = 0.125;
    const availableWidth = pageWidth - leftMargin - rightMargin;

    // Add barcode 
    doc.addImage(barcodeDataUrl, 'PNG', 0.125, barcodeYPosition, 2, 0.28);

    // FNSKU on left, condition on right - smaller fonts
    doc.setFontSize(8); // Smaller font for FNSKU
    doc.text(fnsku, leftMargin, fnskuYPosition, { align: 'left' });
    
    doc.setFontSize(8); // Smaller font for condition
    doc.text(conditionText, pageWidth - rightMargin, fnskuYPosition, { align: 'right' });

    // Product name positioning - larger font for better readability
    doc.setFontSize(8); // Increased font size for product title
    const lineHeight = 0.1; // Increased line height slightly more
    const maxProductNameWidth = pageWidth - leftMargin - rightMargin - 0.05;
    
    // Calculate available height
    const labelBottom = 1.15;
    const availableHeight = labelBottom - productNameStartY - 0.02;
    const maxLines = Math.floor(availableHeight / lineHeight);

    let productName = item.item.title;
    
    // Split product name into multiple lines to fit available space
    const words = productName.split(' ');
    const lines = [];
    let currentLine = '';
    
    // First, try to fit all text normally
    let allTextFits = true;
    let tempLines = [];
    let tempCurrentLine = '';
    
    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
      const word = words[wordIndex];
      const testLine = tempCurrentLine ? tempCurrentLine + ' ' + word : word;
      const testWidth = doc.getTextWidth(testLine);
      
      if (testWidth <= maxProductNameWidth) {
        tempCurrentLine = testLine;
      } else {
        if (tempCurrentLine) {
          tempLines.push(tempCurrentLine);
          tempCurrentLine = word;
        } else {
          tempCurrentLine = word;
        }
        
        if (tempLines.length >= maxLines) {
          allTextFits = false;
          break;
        }
      }
    }
    
    if (tempCurrentLine && tempLines.length < maxLines) {
      tempLines.push(tempCurrentLine);
    } else if (tempCurrentLine || !allTextFits) {
      allTextFits = false;
    }
    
    if (allTextFits) {
      // All text fits, use normal logic
      for (let lineIndex = 0; lineIndex < tempLines.length; lineIndex++) {
        lines.push(tempLines[lineIndex]);
      }
    } else {
      // Text doesn't fit, use beginning + ... + end approach with better space calculation
      const avgCharWidth = doc.getTextWidth('abcdefghijklmnopqrstuvwxyz') / 26;
      const charsPerLine = Math.floor(maxProductNameWidth / avgCharWidth);
      const totalCharBudget = maxLines * charsPerLine * 0.85;
      const ellipsis = '...';
      const ellipsisLength = 3;
      
      if (productName.length <= totalCharBudget) {
        // Fallback to normal truncation if calculation fails
        for (let lineIndex = 0; lineIndex < tempLines.length && lineIndex < maxLines; lineIndex++) {
          lines.push(tempLines[lineIndex]);
        }
      } else {
        // Calculate beginning and end portions more precisely
        const availableChars = totalCharBudget - ellipsisLength;
        const beginningChars = Math.floor(availableChars * 0.65);
        const endingChars = availableChars - beginningChars;
        
        let beginningText = productName.substring(0, beginningChars).trim();
        let endingText = productName.substring(productName.length - endingChars).trim();
        
        // Ensure we break at word boundaries when possible
        const lastSpaceInBeginning = beginningText.lastIndexOf(' ');
        if (lastSpaceInBeginning > beginningChars * 0.7) {
          beginningText = beginningText.substring(0, lastSpaceInBeginning);
        }
        
        const firstSpaceInEnding = endingText.indexOf(' ');
        if (firstSpaceInEnding > 0 && firstSpaceInEnding < endingChars * 0.3) {
          endingText = endingText.substring(firstSpaceInEnding + 1);
        }
        
        const combinedText = beginningText + ellipsis + endingText;
        
        // Split the combined text into lines
        const combinedWords = combinedText.split(' ');
        currentLine = '';
        
        for (let wordIndex = 0; wordIndex < combinedWords.length; wordIndex++) {
          const word = combinedWords[wordIndex];
          const testLine = currentLine ? currentLine + ' ' + word : word;
          const testWidth = doc.getTextWidth(testLine);
          
          if (testWidth <= maxProductNameWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = word;
            }
            
            if (lines.length >= maxLines) {
              break;
            }
          }
        }
        
        if (currentLine && lines.length < maxLines) {
          lines.push(currentLine);
        }
      }
    }
    
    // Print all the lines - CENTER ALIGNED like in the image
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const yPosition = productNameStartY + (lineIndex * lineHeight);
      doc.text(lines[lineIndex], pageWidth / 2, yPosition, { align: 'center', baseline: 'top' });
    }

    if (option === 'sequence' && sequenceSize > 0) {
      // For sequence, print exactly sequenceSize "Ready to Ship" labels after each FNSKU
      for (let j = 0; j < sequenceSize; j++) {
        doc.addPage();
        generateFillerLabel(doc, 'ready');
      }
    } else if (option === 'Ready to Ship') {
      doc.addPage();
      generateFillerLabel(doc, 'ready');
    } else if (option === 'Sold as Set') {
      doc.addPage();
      generateFillerLabel(doc, 'set');
    } else if (option === 'Warning Labels') {
      doc.addPage();
      generateFillerLabel(doc, 'warning');
    } else if (option === 'Suffocation Warning') {
      doc.addPage();
      generateFillerLabel(doc, 'suffocation');
    }
    
    labelCount++;
  }

  if (printer) {
    // Auto print is enabled - send to print server
    console.log(`Auto printing to printer: ${printer}`);

    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(pdfBlob);

      reader.onloadend = async () => {
        try {
          const base64Data = reader.result.split(',')[1];
          const fileName = `${quantity}x_${fnsku}.pdf`;

          const result = await sendPrintJob(printer, base64Data, fileName);
          console.log('Print job submitted successfully:', result);

          if (onPrintCallback) {
            onPrintCallback({
              success: true,
              message: `Print job sent to ${result.printer}`,
              printer: result.printer,
              pdfUrl: pdfUrl
            });
          }
        } catch (error) {
          console.error('Error sending print job:', error);
          if (onPrintCallback) {
            onPrintCallback({
              success: false,
              message: `Error sending print job: ${error.message}`
            });
          }
        }
      };

      reader.onerror = (error) => {
        console.error('Error reading PDF as base64:', error);
        if (onPrintCallback) {
          onPrintCallback({
            success: false,
            message: `Error preparing PDF for printing: ${error.message}`
          });
        }
      };
    } catch (error) {
      console.error('Error sending print job:', error);
      if (onPrintCallback) {
        onPrintCallback({
          success: false,
          message: `Error sending print job: ${error.message}`
        });
      }
    }
  } else {
    // Auto print is disabled - just save the PDF
    doc.save(`${quantity} x ${fnsku}.pdf`);
    
    if (onPrintCallback) {
      onPrintCallback({
        success: true,
        message: `PDF saved: ${quantity} x ${fnsku}.pdf`
      });
    }
  }
};
